import { MutationCache, QueryClient } from "@tanstack/react-query";

import { reportWriteFailure } from "@/lib/writeFailure";

/** Attempts after the first, for anything the server did not refuse outright. */
export const QUERY_RETRIES = 1;

function statusOf(error: unknown): number | undefined {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === "number" ? status : undefined;
}

/**
 * Whether a failed query is worth attempting again.
 *
 * A 429 is not: the server has said this caller is over its budget for the
 * period, so an immediate second attempt cannot succeed and only spends more of
 * the budget. Retrying one is what turned a rate limit into a storm in the QA
 * marathon's second baseline (run 20260909T000907955Z-23e3): 126 refused
 * requests on a single load of /payment, 51 of them for `/api/profile/me/`,
 * filed by the sweep as five `network.duplicate-request` findings and a P0
 * blank page. One refusal per endpoint would have rendered the same page and
 * told the same truth.
 *
 * Everything else keeps the single retry, which is what covers the failure this
 * default exists for: one dropped connection.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (statusOf(error) === 429) return false;
  return failureCount < QUERY_RETRIES;
}

export const queryClient = new QueryClient({
  // FP-103: the one place a failed write is reported, from the mutation's `meta.write`.
  mutationCache: new MutationCache({ onError: reportWriteFailure }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes
      retry: shouldRetryQuery,
      refetchOnWindowFocus: false,
    },
  },
});
